import { FilterField } from "@/components/filters/filter-field";
import { ItemListSectionHeaderRow } from "@/components/item-list/rows/variants/item-list-section-header-row";
import { ItemListRowType } from "@/components/item-list/types";
import { Hotkey } from "@tanstack/react-hotkeys";
import {
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSMenuItem,
  MDSMenuItemVariant,
  MDSModalDialog,
  MDSShortcutKeyCombo,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import {
  StyledShortcutKeyText,
  StyledShortcutKeyWrapper,
} from "@/mds/components/shortcut-key/shortcut-key.style";
import { useScrollbarState } from "@/domains/react/useScrollbarState";
import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import { resolveHotkey } from "./platform";
import { getKeyboardShortcutModalSections } from "./registry";

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsModal({
  isOpen,
  onClose,
}: KeyboardShortcutsModalProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState("");
  const { contentRef, hasVisibleScrollbar } = useScrollbarState({ isEnabled: isOpen });
  const shortcutSections = getKeyboardShortcutModalSections();
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredShortcutSections = useMemo(
    () =>
      shortcutSections.flatMap(section => {
        const filteredShortcuts = section.shortcuts.filter(shortcut => {
          const searchableTerms = [
            shortcut.label,
            shortcut.contextLabel ?? "",
            shortcut.searchHint ?? "",
          ];
          return searchableTerms.some(term =>
            term.trim().toLowerCase().includes(normalizedSearchQuery)
          );
        });
        if (filteredShortcuts.length === 0) {
          return [];
        }

        return [{ ...section, shortcuts: filteredShortcuts }];
      }),
    [normalizedSearchQuery, shortcutSections]
  );

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose}>
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Keyboard Shortcuts
      </MDSText>
      <StyledDialogTextContent
        ref={contentRef}
        slot={MDSDialogSlotName.Content}
        $hasVisibleScrollbar={hasVisibleScrollbar}
      >
        <FilterContainer>
          <FilterField
            placeholder="Search shortcuts"
            value={searchQuery}
            onChange={setSearchQuery}
            onClearText={() => setSearchQuery("")}
            showClearTextButton={true}
            autoFocus={true}
            inputProps={{
              onKeyDown: event => {
                if (event.code !== "Escape") return;
                onClose();
              },
            }}
          />
        </FilterContainer>
        {filteredShortcutSections.map(section => (
          <SectionContainer key={section.section}>
            <PaddedHeader
              data={{
                type: ItemListRowType.SectionHeader,
                key: section.section,
                payload: { title: section.title },
              }}
            />
            {section.shortcuts.map(shortcut => (
              <UnpaddedMenuItem
                key={shortcut.action}
                id={shortcut.action}
                isInert={true}
                label={shortcut.label}
                subtitle={shortcut.contextLabel}
                variant={
                  shortcut.contextLabel ? MDSMenuItemVariant.TwoLine : MDSMenuItemVariant.OneLine
                }
                rightAddon={() => (
                  <ShortcutKeyComboWithSuffix
                    hotkey={resolveHotkey(shortcut.binding)}
                    suffix={shortcut.displayHotkeySuffix}
                  />
                )}
              />
            ))}
          </SectionContainer>
        ))}
      </StyledDialogTextContent>
    </MDSModalDialog>
  );
}

const StyledDialogTextContent = styled(MDSDialogTextContent)<{
  $hasVisibleScrollbar: boolean;
}>`
  padding-right: ${({ $hasVisibleScrollbar, theme }) =>
    $hasVisibleScrollbar ? theme.spacing["2"] : 0};
`;

const FilterContainer = styled.div`
  padding: ${({ theme }) => theme.spacing["2"]} 0px;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.surface.modal};
`;

const PaddedHeader = styled(ItemListSectionHeaderRow)`
  padding-left: 0;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const UnpaddedMenuItem = styled(MDSMenuItem)`
  padding-left: 0px;
  padding-right: 0px;
`;

const ShortcutKeyComboWithSuffix = ({ hotkey, suffix }: { hotkey: Hotkey; suffix?: string }) => {
  return (
    <ShortcutKeyComboContainer>
      <MDSShortcutKeyCombo hotkey={hotkey} size="Large" />
      {suffix
        ? Array.from(suffix).map((token, index) => (
            <ShortcutTokenKeyCap
              key={`${hotkey}-suffix-${token}-${index}`}
              $size="Large"
              $isFixedWidth={true}
            >
              <ShortcutTokenText $size="Large" variant={MDSTextVariant.TextM}>
                {token}
              </ShortcutTokenText>
            </ShortcutTokenKeyCap>
          ))
        : null}
    </ShortcutKeyComboContainer>
  );
};

const ShortcutKeyComboContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const ShortcutTokenKeyCap = styled(StyledShortcutKeyWrapper)`
  white-space: nowrap;
`;

const ShortcutTokenText = styled(StyledShortcutKeyText)`
  white-space: nowrap;
`;
